<template>
  <div class="row">
    <div class="small-12 columns">
      <label
        :for="id"
        class="button"
        :class="buttonClass"
        data-test="file-input"
        v-html="addFileLabel"
      />
      <Field
        v-slot="{ handleChange, handleBlur }"
        name="files"
      >
        <input
          :id="id"
          ref="files"
          type="file"
          :accept="validFileTypes.join(', ')"
          multiple
          class="show-for-sr"
          @change="(e) => {onFileChange($refs.files.files); handleChange(e);}"
          @blur="handleBlur"
        />
      </Field>

      <Field
        v-slot="{ field }"
        :model-value="modelValue"
        :rules="validationString"
        :label="dataVvAs"
        name="files-hidden"
      >
        <input
          id="fileshidden"
          v-bind="field"
          type="hidden"
        />
      </Field>
    </div>
    <div class="small-12 columns">
      <ValidationError
        :overrides="{ required: decodeHTML(dataVvAs) + ' skal vedhæftes', file_already_selected: 'Du må ikke vælge den samme fil to gange' }"
        name="files-hidden"
        data-test="file-input-validation"
      />
    </div>
    <transition
      v-cloak
      name="modal"
    >
      <v-modal
        :is-open="showFileErrorModal"
        data-test="file-error-modal"
        @close="closeModal"
      >
        <template #title>
          <h3 v-html="errorModalTitle" />
        </template>
        <template #content>
          <div>
            <p
              v-if="rejectedFiles.length"
              data-test="rejected-files"
              v-html="outputArrayWithBreaks(rejectedFiles)"
            />
            <p>
              <a
                :href="errorModalFiletypesLinkUrl"
                target="_blank"
                v-html="errorModalFiletypesLinkText"
              />
            </p>
          </div>
        </template>
        <template #buttons>
          <button
            class="button large"
            @click="closeModal"
            v-html="errorModalButtonLabel"
          />
        </template>
      </v-modal>
    </transition>
  </div>
</template>

<script>

import { defineRule } from 'vee-validate';

export default {
  name: 'BaseFileFieldInput',
  emits: ['update:modelValue'],

  props: {
    modelValue: {
      type: Array,
      default() {
        return [];
      }
    },
    title: {
      type: String,
      default: 'Filer:'
    },
    addFileLabel: {
      type: String,
      default: 'Vælg fil'
    },
    maxFileBundleSize: {
      type: Number,
      default: 40960
    },
    dataVvAs: {
      type: String,
      default: 'Vedhæftet fil'
    },
    required: {
      type: Boolean,
      default: true
    },
    buttonClass: {
      type: String,
      default: ''
    },
    errorModalTitle: {
      type: String,
      default: 'Vi modtager desværre ikke den valgte filtype'
    },
    errorModalFiletypesLinkUrl: {
      type: String,
      default: null
    },
    errorModalFiletypesLinkText: {
      type: String,
      default: null
    },
    errorModalButtonLabel: {
      type: String,
      default: 'Tilbage til skaden'
    },
    id: {
      type: String,
      default: 'files'
    },
    allowedFileTypes: {
        type: Array,
        default() {
            return null;
        }
    },
    showInPhotoMode: {
        type: Boolean,
        default: false
    }
  },
  data() {
    return {
      showFileErrorModal: false,
      rejectedFiles: []
    };
  },
  computed: {
    fileBundleSize() {
      var size = 0;
      for (var i in this.modelValue) {
        size += this.bytesToKiloBytes(this.modelValue[i].size);
      }
      return size;
    },
    validFileTypes() {
      if (this.allowedFileTypes !== null) {
        return this.allowedFileTypes;
      }
      
      let fileTypes = [];
      // Text file types
      fileTypes.push('application/pdf');
      fileTypes.push('application/msword');
      fileTypes.push('application/vnd.openxmlformats-officedocument.wordprocessingml.document');
      fileTypes.push('application/xps');
      fileTypes.push('application/oxps');
      fileTypes.push('application/vnd.ms-xpsdocument');
      fileTypes.push('text/plain');
      // Data file types
      fileTypes.push('message/rfc822 eml');
      fileTypes.push('message/rfc82');
      fileTypes.push('text/html');
      fileTypes.push('application/vnd.ms-powerpoint');
      fileTypes.push('application/vnd.openxmlformats-officedocument.presentationml.presentation');
      fileTypes.push('application/xml');
      fileTypes.push('application/vnd.ms-excel');
      fileTypes.push('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
      fileTypes.push('text/x-vcalendar');
      fileTypes.push('text/calendar');
      // Image file types
      fileTypes.push('application/postscript');
      fileTypes.push('image/bmp');
      fileTypes.push('image/jpeg');
      fileTypes.push('image/cr2');
      fileTypes.push('image/gif');
      fileTypes.push('image/png');
      fileTypes.push('application/x-indesign');
      fileTypes.push('application/photoshop');
      // Audio file types
      fileTypes.push('audio/wav');
      fileTypes.push('audio/m4p');
      fileTypes.push('audio/mpeg');
      fileTypes.push('audio/m4a');
      // Video file types
      fileTypes.push('video/3gpp');
      fileTypes.push('video/x-ms-wmv');
      fileTypes.push('video/avi');
      fileTypes.push('video/mp4');
      // Package file types
      fileTypes.push('application/zip');

      return fileTypes;
    },
    validationString() {
      return (
        (this.required ? 'required|' : '') +
        'file_bundle_size:' +
        this.maxFileBundleSize +
        '|file_already_selected|file_size_exists'
      );
    }
  },
  created() {
    var vm = this;

    defineRule('file_already_selected', () => {
      for (var i = 0; i < vm.modelValue.length; i++) {
        var currentItemI = vm.modelValue[i];

        for (var j = 0; j < vm.modelValue.length; j++) {
          if (i !== j) {
            var currentItemJ = vm.modelValue[j];
            if (currentItemI.name === currentItemJ.name) {
              return false;
            }
          }
        }
      }
      return true;
    });

    defineRule('file_size_exists', () => {
      for (var i = 0; i < vm.modelValue.length; i++) {
        var currentItem = vm.modelValue[i];
        if (currentItem.size === 0) {
          return false;
        }
      }
      return true;
    });

    defineRule('file_bundle_size', (value, [size]) => {
      return vm.fileBundleSize < size;
    });
  },
  methods: {
    onFileChange(files) {
      const uploadedFiles = files;
      this.rejectedFiles = [];
      var acceptedFiles = this.modelValue.concat(Array.from(uploadedFiles));
      const emittableFiles = [];

      for (var i = 0; i < acceptedFiles.length; i++) {
        if (acceptedFiles[i].type.length && this.validFileTypes.includes(acceptedFiles[i].type)) {
          emittableFiles.push(acceptedFiles[i]);
        } else {
          this.rejectedFiles.push(acceptedFiles[i].name);
          this.showFileErrorModal = true;
        }
      }

      this.$refs.files.value = '';
      this.$emit('update:modelValue', emittableFiles);
    },
    sizeOf(bytes) {
      if (bytes == 0) {
        return '0.00 B';
      }
      var e = Math.floor(Math.log(bytes) / Math.log(1024));
      return (bytes / Math.pow(1024, e)).toFixed(2) + ' ' + ' KMGTP'.charAt(e) + 'B';
    },
    bytesToKiloBytes(byteValue) {
      return Math.ceil(byteValue / 1024);
    },
    decodeHTML(html) {
      var txt = document.createElement('textarea');
      txt.innerHTML = html;
      return txt.value.replace(/<(?!br\s*\/?)[^>]+>/g, '');
    },
    closeModal() {
      this.showFileErrorModal = false;
    },
    outputArrayWithBreaks(array) {
      return array.join('<br />');
    }
  }
};
</script>
